import { getSsoUrl, getServiceId } from 'common/axios/getApiUrl';

// 토큰 관련 상수
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_INFO_KEY = 'userInfo';

// SSO 팝업 참조
let ssoPopup: Window | null = null;

/**
 * 네이티브 WebView 환경인지 확인
 */
const isNativeWebView = (): boolean => {
    return !!window.ReactNativeWebView;
};

/**
 * Access Token 가져오기
 */
export const getAccessToken = (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Refresh Token 가져오기
 */
export const getRefreshToken = (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/* 방금 로그아웃했다는 표시.
 *
 * 앱에서 로그아웃하면 웹은 네이티브에 CLEAR_AUTH 를 보내고 기다리지 않은 채
 * /login 으로 다시 로드합니다. 그 사이 네이티브는 페이지 로드가 끝날 때마다
 * MMKV 에서 토큰을 읽어 SEND_TOKEN 으로 밀어주는데(handleLoadEnd),
 * 아직 CLEAR_AUTH 를 처리하기 전이면 지운 토큰이 그대로 다시 날아옵니다.
 * 그걸 받아 localStorage 에 넘기면 로그아웃이 무효가 됩니다.
 *
 * 그래서 로그아웃 때 표시를 세워두고, 이 표시가 있는 동안은
 * 자동으로 들어오는 토큰을 무시합니다. 표시는 진짜 로그인(setTokens)이
 * 일어나면 지워집니다. sessionStorage 라 앱을 닫으면 같이 사라집니다. */
const JUST_LOGGED_OUT_KEY = 'justLoggedOut';

/** 로그아웃했음을 표시. userStore.onLogout 에서 부릅니다. */
export const markLoggedOut = (): void => {
    try { sessionStorage.setItem(JUST_LOGGED_OUT_KEY, '1'); } catch {}
};

/** 로그아웃 표시를 거둡니다. setTokens 을 거치지 않는 로그인 경로에서 부릅니다. */
export const clearLoggedOutMark = (): void => {
    try { sessionStorage.removeItem(JUST_LOGGED_OUT_KEY); } catch {}
};

/** 로그아웃 직후인가. 읽기만 하고 지우지 않습니다 —
 *  SEND_TOKEN 은 언제 도착할지 몰라 한 번 읽고 치우면 다음 하나를 놓칩니다. */
export const isJustLoggedOut = (): boolean => {
    try { return !!sessionStorage.getItem(JUST_LOGGED_OUT_KEY); } catch { return false; }
};

/**
 * 토큰 저장하기
 * - 웹 localStorage 저장
 * - 네이티브 앱 환경이면 브릿지로 토큰 전달해 위치 API 호출에 사용
 */
export const setTokens = (accessToken: string, refreshToken?: string): void => {
    // 진짜 로그인이 일어났으니 로그아웃 표시를 거둡니다.
    clearLoggedOutMark();
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    // 네이티브 웹뷰 환경이면 브릿지로 토큰 전달 (dokocrew_native SEND_TOKEN 수신 핸들러가 저장)
    try {
        const { requestApp, WEB_TO_APP, isNativeApp } = require('./../webview');
        if (isNativeApp?.()) {
            requestApp(WEB_TO_APP.SEND_TOKEN, {
                accessToken,
                refreshToken,
                access_token: accessToken,
                refresh_token: refreshToken,
            });
        }
    } catch { /* no-op — webview 모듈 없거나 네이티브 환경 아님 */ }
};

/**
 * 토큰 삭제하기
 */
export const clearTokens = (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
};

/**
 * 로그인 상태 확인
 *
 * accessToken 이 만료된 경우라도 refreshToken 이 있으면 axios interceptor 가 갱신할 수 있으므로
 * "logged in" 으로 간주. 그래야 ProtectedRoute → CostInput 같은 페이지 진입 후 첫 API 호출에서
 * 401 이 발생할 때 기존 refresh 흐름으로 토큰 갱신 + 재시도가 가능.
 *
 * 회귀: 이전 구현은 access 만료 즉시 clearTokens() 후 false 반환 → ProtectedRoute 가 /login 으로
 * redirect 시켜 axios refresh 흐름을 거칠 기회조차 없었음. 정산하기 등 일정 시간 후 페이지 이동
 * 시점에서 갑자기 로그아웃 처리되는 사용자 보고가 이 경로로 발생.
 */
/**
 * JWT payload(2번째 segment) 를 안전하게 디코딩.
 * - base64url(-, _) → base64(+, /) 치환
 * - 패딩 보정
 * - Latin-1 바이트 → UTF-8 재해석 (한글 등 멀티바이트 보존)
 *
 * 이전 구현(atob + JSON.parse 직접)은 payload 에 URL-safe 문자가 들어가거나 닉네임이
 * 한글이면 throw → clearTokens 로 강제 로그아웃되는 회귀가 있었음 (특히 일부 신규 가입자).
 */
export const decodeJwtPayload = (token: string): any => {
    const seg = token.split('.')[1];
    if (!seg) return null;
    let base64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    const utf8 = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(utf8);
};

/**
 * 토큰이 만료됐는가.
 *
 * 형식을 못 읽거나 exp 가 없으면 만료로 보지 않습니다 — JWT 가 아닌 refresh
 * 토큰을 쓰는 환경에서 멀쩡한 세션을 끊지 않기 위해서입니다.
 */
export const isTokenExpired = (token: string): boolean => {
    try {
        const payload = decodeJwtPayload(token);
        if (typeof payload?.exp !== 'number') return false;
        return payload.exp * 1000 < Date.now();
    } catch {
        return false;
    }
};

/**
 * 이 모임에 게스트(비회원, X-Guest-Token)로 들어와 있는지.
 * 게스트 토큰은 dokocrew-api 만 알아보므로 wiz-common·wizai 를 직접 부르는 기능(영수증 사진 인식 등)은 게스트에게 감춘다.
 */
export const isGuestOf = (meetingId?: string): boolean => {
    if (!meetingId || isLoggedIn()) return false;
    try {
        return !!sessionStorage.getItem('guestToken') && sessionStorage.getItem('guestMeetingId') === meetingId;
    } catch {
        return false;
    }
};

export const isLoggedIn = (): boolean => {
    const accessToken = getAccessToken();
    if (!accessToken) return false;

    try {
        const payload = decodeJwtPayload(accessToken);
        if (payload?.exp && payload.exp * 1000 < Date.now()) {
            // access 만료 — refresh 가능 여부 판단
            const refreshToken = getRefreshToken();
            // refresh 토큰의 만료까지 봅니다. 존재만 확인하면, 둘 다 만료된 기기가
            // 로그인처럼 보이면서 모든 요청이 401 로 끝나는 상태로 남습니다.
            if (refreshToken && !isTokenExpired(refreshToken)) return true; // axios interceptor 가 401 시점에 갱신
            // refresh 가 없거나 그것도 만료면 진짜 로그아웃
            clearTokens();
            return false;
        }
    } catch {
        // JWT 파싱 실패 — refresh 도 활용 못 하므로 토큰 정리
        clearTokens();
        return false;
    }

    return true;
};

/**
 * SSO 팝업을 열고 postMessage로 토큰을 수신하는 함수 (웹 브라우저 전용)
 */
const openSsoPopupWeb = (
    url: string,
    name: string,
    onSuccess?: (data: { access_token: string; refresh_token: string; user_id: string; user_no?: string }) => void
): void => {
    const ssoUrl = getSsoUrl();

    // 기존 팝업이 열려있으면 포커스
    if (ssoPopup && !ssoPopup.closed) {
        ssoPopup.focus();
        return;
    }

    // 팝업 중앙 위치 계산
    const width = 480;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    ssoPopup = window.open(
        url,
        name,
        `width=${width},height=${height},left=${left},top=${top},noopener=no,noreferrer=no`
    );

    // postMessage 리스너 등록
    const handleMessage = (e: MessageEvent) => {
        const ssoOrigin = new URL(ssoUrl).origin;
        if (e.origin !== ssoOrigin) return;

        const { type, data } = e.data || {};

        if (type === 'SEND_TOKEN' && data?.access_token) {
            setTokens(data.access_token, data.refresh_token);
            onSuccess?.(data);
            window.removeEventListener('message', handleMessage);
            ssoPopup = null;
        }
    };

    window.addEventListener('message', handleMessage);
};

/**
 * SSO 페이지로 직접 이동 (네이티브 WebView 또는 팝업 차단 환경)
 * 로그인 완료 후 returnUrl로 토큰을 쿼리 파라미터에 담아 리다이렉트됨
 */
const redirectToSso = (url: string, returnUrl: string): void => {
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = `${url}${separator}returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = fullUrl;
};

/**
 * SSO 로그인 - 환경에 따라 팝업 또는 리다이렉트
 * - 웹 브라우저: 팝업으로 열고 postMessage로 토큰 수신
 * - 네이티브 WebView: SSO 페이지로 직접 이동, 로그인 후 returnUrl로 리다이렉트
 */
export const openSsoLoginPopup = (
    onSuccess?: (data: { access_token: string; refresh_token: string; user_id: string; user_no?: string }) => void
): void => {
    const ssoUrl = getSsoUrl();
    const serviceId = getServiceId();
    const origin = window.location.origin;
    const deviceId = window.__DEVICE__?.deviceId || 'web-device';
    const os = window.__DEVICE__?.os || 'web';

    const loginUrl = `${ssoUrl}?serviceId=${encodeURIComponent(serviceId)}&origin=${encodeURIComponent(origin)}&deviceId=${encodeURIComponent(deviceId)}&os=${encodeURIComponent(os)}`;

    if (isNativeWebView()) {
        // 네이티브: SSO로 직접 이동. 로그인 완료 후 /login?access_token=...&... 로 돌아옴
        const returnUrl = `${origin}/login`;
        redirectToSso(loginUrl, returnUrl);
    } else {
        openSsoPopupWeb(loginUrl, 'sso-login', onSuccess);
    }
};

/**
 * SSO 회원가입
 */
export const openSsoSignupPopup = (
    onSuccess?: (data: { access_token: string; refresh_token: string; user_id: string; user_no?: string }) => void
): void => {
    const ssoUrl = getSsoUrl();
    const serviceId = getServiceId();
    const origin = window.location.origin;

    const deviceId = window.__DEVICE__?.deviceId || 'web-device';
    const os = window.__DEVICE__?.os || 'web';
    const signupUrl = `${ssoUrl}/sign-up?serviceId=${encodeURIComponent(serviceId)}&origin=${encodeURIComponent(origin)}&deviceId=${encodeURIComponent(deviceId)}&os=${encodeURIComponent(os)}`;

    if (isNativeWebView()) {
        const returnUrl = `${origin}/login`;
        redirectToSso(signupUrl, returnUrl);
    } else {
        openSsoPopupWeb(signupUrl, 'sso-signup', onSuccess);
    }
};

/**
 * URL hash fragment에서 토큰 추출 (SSO 리다이렉트 후 콜백 처리)
 * hash fragment는 서버로 전송되지 않으므로 보안상 안전
 * 예: /login#access_token=xxx&refresh_token=yyy&user_id=zzz
 * @returns 토큰 데이터 또는 null
 */
export const extractTokensFromUrl = (): { access_token: string; refresh_token: string; user_id: string; user_no?: string } | null => {
    const hash = window.location.hash;
    if (!hash || hash.length <= 1) return null;

    // # 제거 후 파싱
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const userId = params.get('user_id');
    const userNo = params.get('user_no');

    if (accessToken) {
        // 토큰 저장
        setTokens(accessToken, refreshToken || undefined);

        // URL에서 hash 즉시 제거 (보안)
        window.history.replaceState({}, '', window.location.pathname + window.location.search);

        return {
            access_token: accessToken,
            refresh_token: refreshToken || '',
            user_id: userId || '',
            user_no: userNo || ''
        };
    }

    return null;
};

/**
 * SSO 로그인 페이지로 이동
 */
export const redirectToSsoLogin = (): void => {
    openSsoLoginPopup();
};

/**
 * SSO 회원가입 페이지로 이동
 */
export const redirectToSsoSignup = (): void => {
    openSsoSignupPopup();
};

/**
 * 인증이 필요한 페이지에서 사용할 함수
 */
export const requireAuth = (): boolean => {
    if (!isLoggedIn()) {
        redirectToSsoLogin();
        return false;
    }
    return true;
};
